import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Button, Text } from '@factorialco/deprecated-design-system'
import {
  ContractVersionPageContractVersionFragment,
  ContractVersionsPageEmployeeFragment,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import useGraphqlContracts from 'modules/contracts/hooks/useGraphlContracts'
import type GraphqlContract from 'modules/contracts/lib/graphqlContract'
import {
  hasOngoing,
  hasOngoingOrUpcomingContract,
} from 'modules/contracts/lib/graphqlContractVersionSchedule'

import ExpiredOrUpcomingModalContent from './Modals/ExpiredOrUpcomingModalContent'
import OngoingModalContent from './Modals/OngoingModalContent'
import { FLOWS } from './constants'

const getTitle = (contracts: GraphqlContract[], employeeFullName: string) => {
  if (hasOngoing(contracts))
    return i18n.t('contract_versions.modals.quick_flows.title.ongoing_with_name', {
      employeeFullName: employeeFullName,
    })

  return i18n.t('contract_versions.modals.quick_flows.title.upcoming_with_name', {
    employeeFullName: employeeFullName,
  })
}

interface Props {
  employee: ContractVersionsPageEmployeeFragment
  onCloseModal: () => void
  selectedContractVersion: ContractVersionPageContractVersionFragment
  allContractVersions: ContractVersionPageContractVersionFragment[]
  setOption: React.Dispatch<React.SetStateAction<string>>
  option: string
  showFixOption?: boolean
  hasToRequestUpdate: boolean
}

const FlowSelectorModal = ({
  employee,
  onCloseModal,
  selectedContractVersion,
  allContractVersions,
  setOption,
  option,
  showFixOption,
  hasToRequestUpdate,
}: Props) => {
  const { contracts: graphqlContracts } = useGraphqlContracts(
    selectedContractVersion,
    allContractVersions
  )

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const canPromote = selectedContractVersion._policies?.promote?.can ?? false
  const canEdit = selectedContractVersion._policies?.edit?.can ?? false
  const employeeFullName = employee?.fullName ?? ''

  const title = getTitle(graphqlContracts, employeeFullName)

  const onChange = (selectedOption: React.SetStateAction<string>) => {
    setOption(selectedOption)
  }

  const getContractStatus = () => {
    if (hasOngoing(graphqlContracts)) return 'ongoing'
    if (!hasOngoingOrUpcomingContract(graphqlContracts)) return 'expired'

    return 'upcoming'
  }

  const withoutContracts = allContractVersions.length === 0

  const onPromote = () => {
    track(trackingEvents.employee.contracts.newContractConditionsStart, {
      contract_status: getContractStatus(),
      update_type: FLOWS.promotion,
      approval: hasToRequestUpdate,
    })
    navigate(
      navigation.employees.id.contractVersions.promotion.fullPath({
        id: employee?.id.toString() ?? '',
      })
    )
  }

  const onFix = () => {
    track(trackingEvents.employee.contracts.fixContractStart, {
      contract_status: getContractStatus(),
    })
    navigate(
      navigation.employees.id.contractVersions.fix.fullPath({
        id: employee?.id.toString() ?? '',
        contractVersionId: selectedContractVersion.id ?? '',
      })
    )
  }

  const onEvolve = () => {
    track(trackingEvents.employee.contracts.newContractConditionsStart, {
      contract_status: getContractStatus(),
      update_type: FLOWS.evolution,
    })
    navigate(
      navigation.employees.id.contractVersions.update.fullPath({
        id: employee?.id.toString() ?? '',
      })
    )
  }

  const onSubmit = () => {
    switch (option) {
      case FLOWS.promotion:
        onPromote()
        break
      case FLOWS.evolution:
      case FLOWS.creation:
        onEvolve()
        break
      case FLOWS.fix:
        onFix()
        break
      default:
        throw new Error(`Case ${option} not found.`)
    }
  }

  let modalContent = undefined

  if (hasOngoing(graphqlContracts) && canPromote) {
    modalContent = (
      <OngoingModalContent
        showFixOption={showFixOption ?? true}
        employeeFullName={employeeFullName}
        value={option}
        onChange={onChange}
        canPromote={canPromote}
        canEdit={canEdit}
      />
    )
  } else {
    modalContent = (
      <ExpiredOrUpcomingModalContent
        showFixOption={showFixOption ?? true}
        employeeFullname={employeeFullName}
        value={option}
        onChange={onChange}
        withoutContracts={withoutContracts}
      />
    )
  }

  const modalDescription = (
    <>
      {modalContent}
      <Text.Normal weight="normal" size="200" color="grey800">
        {i18n.t('contract_versions.modals.quick_flows.footer')}
      </Text.Normal>
    </>
  )

  return (
    <Modal
      title={title}
      illustration="table-file"
      onSubmit={onSubmit}
      onClose={onCloseModal}
      description={modalDescription}
      footer={
        <>
          <Button disabled={false} onClick={onCloseModal} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button submit>{i18n.t('common.next')}</Button>
        </>
      }
    />
  )
}

export default FlowSelectorModal
